import type { JSX } from 'react';

import { Box, useTheme } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import { copyText } from '@/utils/clipboard';
import { findPhoneSegments } from '@/utils/phone';

// Renders a free-text Kontakt / Poznámka value with every recognised phone
// number turned into a clickable run (prototype `.tel`: mono, primary, dotted
// underline). CHR-06 §6: a click copies the CANONICAL `+420…` form (not the
// text as typed) and toasts „Telefon zkopírován"; on the mobile card tree the
// run is additionally a `tel:` link so the same tap can dial.
//
// Shared by the desktop table and the card tree so the recognition rule and the
// feedback are identical in both.

const TAP_TARGET_MIN_PX = 44;

export interface PhoneTextProps {
  text: string | null;
  // Mobile: render the run as a `tel:` anchor (it still copies on tap).
  dial?: boolean;
  // Shown when the value is empty — the caller decides („—" vs. the longer
  // „adresa v tabulce chybí" wording).
  emptyLabel?: string;
  'data-testid'?: string;
}

export const PhoneText = ({
  text,
  dial = false,
  emptyLabel,
  'data-testid': dataTestId,
}: PhoneTextProps): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation('places');
  const { enqueueSnackbar } = useSnackbar();

  const value = text?.trim() ?? '';
  if (value.length === 0) {
    return (
      <Box
        component="span"
        data-testid={dataTestId}
        sx={{ color: theme.palette.text.disabled }}
      >
        {emptyLabel ?? t('value.empty')}
      </Box>
    );
  }

  const handleCopy = async (phone: string): Promise<void> => {
    const ok = await copyText(phone);
    enqueueSnackbar(ok ? t('toast.phoneCopied') : t('toast.copyFailed'), {
      variant: ok ? 'success' : 'error',
    });
  };

  const phoneSx = {
    fontFamily: theme.monospaceFontFamily,
    fontVariantNumeric: 'tabular-nums',
    fontSize: 'inherit',
    color: theme.palette.primary.main,
    textDecoration: 'underline dotted',
    textUnderlineOffset: '2px',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    p: 0,
    // Touch surfaces get the full tap target; the desktop table keeps the run
    // inline so it does not blow up the row height.
    ...(dial
      ? { display: 'inline-flex', alignItems: 'center', minHeight: TAP_TARGET_MIN_PX }
      : { display: 'inline' }),
    '&:hover': { color: theme.palette.primary.dark },
  } as const;

  return (
    <Box component="span" data-testid={dataTestId}>
      {findPhoneSegments(value).map((segment, index) =>
        segment.phone === null ? (
          <Box component="span" key={`t-${index}`}>
            {segment.text}
          </Box>
        ) : dial ? (
          <Box
            component="a"
            key={`p-${index}`}
            href={`tel:${segment.phone}`}
            data-testid="place-phone"
            aria-label={t('phone.callAria', { phone: segment.phone })}
            onClick={() => {
              void handleCopy(segment.phone as string);
            }}
            sx={phoneSx}
          >
            {segment.text}
          </Box>
        ) : (
          <Box
            component="button"
            type="button"
            key={`p-${index}`}
            data-testid="place-phone"
            aria-label={t('phone.copyAria', { phone: segment.phone })}
            onClick={() => {
              void handleCopy(segment.phone as string);
            }}
            sx={phoneSx}
          >
            {segment.text}
          </Box>
        ),
      )}
    </Box>
  );
};
